export const REFERENCE_TAG = "reference";

// Reference tags are generated by this feature, so the supported grammar is
// deliberately limited to double-quoted string attributes.
export const REFERENCE_ATTRS_PATTERN = '(?:\\s+[A-Za-z][\\w-]*="[^"]*")*';

const REFERENCE_TAG_RE = new RegExp(
  `<${REFERENCE_TAG}(${REFERENCE_ATTRS_PATTERN})\\s*/>`,
  "g",
);

const ATTR_RE = /([A-Za-z][\w-]*)="([^"]*)"/g;

// MDX emits `"` as `&#x22;`; the remaining entities come from our manual
// serializer. Decode `&amp;` last to avoid decoding nested entities twice.
const decodeAttrValue = (value: string): string =>
  value
    .replace(/&#x22;|&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

const encodeAttrValue = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&#x22;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const parseReferenceAttributes = (raw: string): Record<string, string> =>
  Object.fromEntries(
    [...raw.matchAll(ATTR_RE)].map((match) => [
      match[1],
      decodeAttrValue(match[2]),
    ]),
  );

// Persisted mention blocks must become reference tags before passing through
// the Markdown renderer, so this path cannot rely on the editor's serializer.
export const serializeReference = (
  attributes: Record<string, string>,
): string =>
  `<${REFERENCE_TAG}${Object.entries(attributes)
    .map(([name, value]) => ` ${name}="${encodeAttrValue(value)}"`)
    .join("")} />`;

// A reference tag alone on a line starts a CommonMark HTML block that
// swallows every line up to the next blank one, so the remark plugin never
// sees the tag and the transcript would show raw markup.
const STANDALONE_REFERENCE_LINE_RE = new RegExp(
  `(^|\\n)([ \\t]*<${REFERENCE_TAG}${REFERENCE_ATTRS_PATTERN}\\s*/>[ \\t]*)\\n(?=[^\\n]*\\S)`,
  "g",
);

/**
 * Folds the line break after a reference tag that stands alone on a line into
 * a space, keeping the tag inline paragraph content. Rendering is unchanged:
 * soft line breaks collapse to spaces anyway.
 */
export const inlineStandaloneReferences = (markdown: string): string =>
  markdown.replace(STANDALONE_REFERENCE_LINE_RE, "$1$2 ");

export type ReferenceContentPart =
  | { type: "text"; text: string }
  | {
      type: "reference";
      attributes: Record<string, string>;
      raw: string;
    };

/**
 * Splits editor Markdown into an ordered sequence that can be mapped directly
 * to the API's text and mention content blocks.
 */
export const parseReferenceContent = (
  markdown: string,
): ReferenceContentPart[] => {
  const matches = [...markdown.matchAll(REFERENCE_TAG_RE)];
  const parts = matches.flatMap((match, index): ReferenceContentPart[] => {
    const previousMatch = matches[index - 1];
    const previousEnd =
      previousMatch === undefined
        ? 0
        : (previousMatch.index ?? 0) + previousMatch[0].length;
    const text = markdown.slice(previousEnd, match.index ?? previousEnd);
    return [
      ...(text
        ? [{ type: "text" as const, text: text.replace(/\\</g, "<") }]
        : []),
      {
        type: "reference" as const,
        attributes: parseReferenceAttributes(match[1] ?? ""),
        raw: match[0],
      },
    ];
  });
  const lastMatch = matches.at(-1);
  const tailStart =
    lastMatch === undefined ? 0 : (lastMatch.index ?? 0) + lastMatch[0].length;
  const tail = markdown.slice(tailStart).replace(/\\</g, "<");

  return [...parts, ...(tail ? [{ type: "text" as const, text: tail }] : [])];
};

export const extractReferences = (markdown: string): Record<string, string>[] =>
  parseReferenceContent(markdown).flatMap((part) =>
    part.type === "reference" ? [part.attributes] : [],
  );
